// localStorage-backed reactive value. Keys are namespaced `v2:<key>:<version>` so they never
// collide with the old client's `<key>:1` entries on the same origin; `legacyKey` imports the
// old value once when no v2 value exists yet.
import { browser } from '$app/environment';

const VERSION = 1;

/** @template T @param {T} value @returns {T} */
function clone(value) {
	return value !== null && typeof value === 'object' ? structuredClone(value) : value;
}

/**
 * A restored value must keep the shape of an array/object initial: the old client stored `""` in
 * `delivery_address` after an order, and a string there breaks every `value.x = …` write.
 * @param {unknown} restored @param {unknown} initial
 */
function sameShape(restored, initial) {
	if (Array.isArray(initial)) return Array.isArray(restored);
	if (initial !== null && typeof initial === 'object') {
		return restored !== null && typeof restored === 'object' && !Array.isArray(restored);
	}
	return true;
}

/**
 * @template T
 * @param {string} key
 * @param {T} initial
 * @param {{ legacyKey?: string }} [options]
 * @returns {{ value: T, reset: () => void, readonly key: string }}
 */
export function persisted(key, initial, options = {}) {
	const storageKey = `v2:${key}:${VERSION}`;
	let start = clone(initial);

	if (browser) {
		try {
			let json = localStorage.getItem(storageKey);
			if (json === null && options.legacyKey) {
				json = localStorage.getItem(`${options.legacyKey}:1`);
			}
			if (json !== null && json !== 'undefined') {
				const restored = JSON.parse(json);
				if (sameShape(restored, initial)) start = restored;
			}
		} catch (e) {
			console.warn(`persisted(${key}): could not restore`, e);
		}
	}

	const store = $state({ value: start });

	if (browser) {
		// Module-level root: lives for the whole page, intentionally never disposed.
		$effect.root(() => {
			$effect(() => {
				// Reading the snapshot subscribes to every nested property, so deep mutations persist too.
				const snapshot = $state.snapshot(store.value);
				try {
					localStorage.setItem(storageKey, JSON.stringify(snapshot));
				} catch (e) {
					console.warn(`persisted(${key}): could not save`, e);
				}
			});
		});
	}

	return {
		get value() {
			return store.value;
		},
		set value(v) {
			store.value = v;
		},
		reset() {
			store.value = clone(initial);
		},
		get key() {
			return storageKey;
		}
	};
}
